import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useT } from '../../i18n';

/** Un globo del recorrido: resalta el elemento con `data-tour="<element>"` y muestra el texto. */
export interface TourStep {
  screen: string;
  element: string;
  text: string;
}

interface GuidedTourProps {
  steps: TourStep[];
  /** Al terminar o saltar el recorrido (queda como visto). */
  onFinish: () => void;
}

/** «Toca **Guardar**» → «Toca <strong>Guardar</strong>» (las guías marcan así los textos de pantalla). */
export const withBold = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>,
  );

const findElement = (name: string) => document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`);

/**
 * Recorrido guiado por la pantalla: oscurece el resto, resalta un botón o campo a la vez y explica qué
 * hacer, con «Anterior», «Siguiente» y «Saltar». Los pasos cuyo elemento no está en pantalla (por
 * permisos, por ejemplo) se omiten. Esc lo cierra.
 */
const GuidedTour: React.FC<GuidedTourProps> = ({ steps, onFinish }) => {
  const t = useT();
  const [available] = useState(() => steps.filter((s) => findElement(s.element)));
  const [index, setIndex] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const step = available[index];

  // Sin elementos en pantalla no hay nada que mostrar: termina sin molestar.
  useEffect(() => {
    if (!available.length) onFinish();
  }, [available.length, onFinish]);

  const measure = useCallback(() => {
    if (anchor) setRect(anchor.getBoundingClientRect());
  }, [anchor]);

  useLayoutEffect(() => {
    if (!step) return;
    const el = findElement(step.element);
    setAnchor(el);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // Vuelve a medir cuando termina el desplazamiento suave.
      const timer = window.setTimeout(() => setRect(el.getBoundingClientRect()), 350);
      setRect(el.getBoundingClientRect());
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFinish();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [measure, onFinish]);

  if (!step || !anchor || !rect) return null;
  const last = index === available.length - 1;
  const pad = 6;

  return (
    <>
      {/* Capa oscura alrededor del elemento: cuatro rectángulos (arriba, abajo, izquierda, derecha). */}
      {(() => {
        const x = Math.max(0, rect.left - pad);
        const y = Math.max(0, rect.top - pad);
        const w = rect.width + pad * 2;
        const h = rect.height + pad * 2;
        const pieces = [
          { top: 0, left: 0, right: 0, height: y },
          { top: y + h, left: 0, right: 0, bottom: 0 },
          { top: y, left: 0, width: x, height: h },
          { top: y, left: x + w, right: 0, height: h },
        ];
        return pieces.map((piece, i) => (
          <Box
            key={i}
            aria-hidden
            sx={(theme) => ({
              position: 'fixed',
              zIndex: theme.zIndex.modal + 1,
              bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.65 : 0.5),
              pointerEvents: 'none',
              ...piece,
            })}
          />
        ));
      })()}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'fixed',
          zIndex: theme.zIndex.modal + 1,
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          borderRadius: 1.5,
          outline: `2px solid ${theme.palette.primary.main}`,
          pointerEvents: 'none',
          transition: 'all 0.2s ease',
        })}
      />
      <Popper
        open
        anchorEl={anchor}
        placement="bottom"
        modifiers={[
          { name: 'offset', options: { offset: [0, 14] } },
          { name: 'flip', options: { fallbackPlacements: ['top', 'right', 'left'] } },
          { name: 'preventOverflow', options: { padding: 12 } },
        ]}
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 2 })}
      >
        <Paper
          elevation={8}
          role="dialog"
          aria-live="polite"
          aria-label={t('screenHelp.tourLabel')}
          sx={{ p: 2, width: { xs: 'calc(100vw - 32px)', sm: 340 }, maxWidth: 360, borderTop: 3, borderColor: 'primary.main' }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('screenHelp.stepOf', { n: index + 1, total: available.length })}
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, mb: 2, overflowWrap: 'anywhere' }}>
            {withBold(step.text)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" color="inherit" onClick={onFinish}>
              {t('screenHelp.skip')}
            </Button>
            <Box sx={{ flex: 1 }} />
            {index > 0 && (
              <Button size="small" onClick={() => setIndex((i) => i - 1)}>
                {t('screenHelp.previous')}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={() => (last ? onFinish() : setIndex((i) => i + 1))} autoFocus>
              {last ? t('screenHelp.done') : t('screenHelp.next')}
            </Button>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export default GuidedTour;
